import { httpsCallable } from "firebase/functions";
import type { Firestore } from "firebase/firestore";
import { functions } from "../firebase";
import { PlanService } from "../services/PlanService";
import { UserType } from "../types/plan";

export const PENDING_CONSTELLATION_KEY = "pendingConstellationAppointment";

export type PendingConstellationAppointment = {
  therapistId: string;
  therapistName?: string;
  appointmentDateTime: string;
  planId?: string | null;
  permiteParticipantes?: boolean;
  sessionDuration?: number;
  constellationOffer?: "social" | "plataforma";
  savedAt?: string;
};

export function readPendingConstellationAppointment(): PendingConstellationAppointment | null {
  try {
    const raw = sessionStorage.getItem(PENDING_CONSTELLATION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingConstellationAppointment;
    if (!parsed?.therapistId || !parsed?.appointmentDateTime) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function savePendingConstellationAppointment(
  pending: PendingConstellationAppointment,
): void {
  sessionStorage.setItem(
    PENDING_CONSTELLATION_KEY,
    JSON.stringify({
      ...pending,
      savedAt: new Date().toISOString(),
    }),
  );
}

export function clearPendingConstellationAppointment(): void {
  sessionStorage.removeItem(PENDING_CONSTELLATION_KEY);
}

/**
 * Resolve o planId real do Firestore para checkout de constelação.
 * Prefere "Valor Social", depois o plano de constelação mais barato.
 */
export async function resolveConstellationCheckoutPlanId(
  db: Firestore,
  offer?: string | null,
): Promise<string | null> {
  const plans = await PlanService.fetchActivePlans(db);
  const constellation = plans.filter((plan) => {
    if (plan.userType !== UserType.PATIENT) return false;
    const blob = `${plan.name} ${plan.sessionLabel || ""}`.toLowerCase();
    if (/particip|represent|clube/.test(blob)) return false;
    return /constel|sessão|sessao/.test(blob);
  });

  if (constellation.length === 0) return null;

  const social = constellation.find((plan) =>
    /social|valor social/.test(plan.name.toLowerCase()),
  );
  const platform = constellation.find((plan) =>
    !/social|valor social/.test(plan.name.toLowerCase()),
  );

  if (offer === "social") return social?.id || constellation[0]?.id || null;
  if (platform?.id) return platform.id;
  constellation.sort((a, b) => (a.value || 0) - (b.value || 0));
  return constellation[0]?.id ?? null;
}

function isCreditFailureMessage(message?: string): boolean {
  const m = String(message || "").toLowerCase();
  return (
    m.includes("crédito") ||
    m.includes("credito") ||
    m.includes("adquira um plano") ||
    m.includes("plano")
  );
}

export function isConstellationCreditFailure(error: {
  code?: string;
  message?: string;
}): boolean {
  if (error.code !== "functions/failed-precondition") return false;
  return isCreditFailureMessage(error.message);
}

/**
 * Após o pagamento liberar o crédito, cria o agendamento que ficou pendente.
 * Faz poucas retentativas curtas porque o crédito pode chegar com leve atraso.
 */
export async function finalizePendingConstellationAppointment(): Promise<{
  appointmentId?: string;
  message?: string;
} | null> {
  const pending = readPendingConstellationAppointment();
  if (!pending) return null;

  const createAppointment = httpsCallable(functions, "createConstellationAppointment");
  const delays = [0, 1500, 3000, 5000];

  for (const delay of delays) {
    if (delay > 0) {
      await new Promise((resolve) => setTimeout(resolve, delay));
    }

    try {
      const result = await createAppointment({
        therapistId: pending.therapistId,
        appointmentDateTime: pending.appointmentDateTime,
        sessionDuration: pending.sessionDuration || 60,
        permiteParticipantes: Boolean(pending.permiteParticipantes),
      });
      const data = result.data as {
        success?: boolean;
        appointmentId?: string;
        message?: string;
      };

      if (data?.success) {
        clearPendingConstellationAppointment();
        return {
          appointmentId: data.appointmentId,
          message:
            data.message ||
            "Pagamento confirmado e sua sessão foi agendada com o horário escolhido.",
        };
      }
    } catch (error: unknown) {
      const fe = error as { code?: string; message?: string };
      // Ainda sem crédito — tenta de novo. Outros erros interrompem.
      if (isConstellationCreditFailure(fe)) continue;
      throw error;
    }
  }

  return null;
}
